import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MapPin, Building2, Check, Clock, X, UserCheck } from "lucide-react";
import { notificationsApi, discoveryApi, assignApi, usersApi, signalsApi, apiErrorMessage } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { PriorityBadge, labelize } from "@/components/ProspectCard";
import { EmptyState } from "@/components/EmptyState";
import { Sheet } from "@/components/Sheet";
import { toast } from "sonner";

const TYPE_LABEL = { fsa_new_caterctrl_prospect: "New CaterCTRL Prospect", fhrs_rating_changed: "Rating Changed", fsa_new_establishment: "New Establishment" };

export default function Notifications() {
  const navigate = useNavigate();
  const { isManager } = useAuth();
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("unread");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snoozeFor, setSnoozeFor] = useState(null);
  const [assignFor, setAssignFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await notificationsApi.list({ status: statusFilter || undefined, size: 100 });
      setItems(typeFilter ? data.items.filter((n) => n.notification_type === typeFilter) : data.items);
    } finally { setLoading(false); }
  }, [statusFilter, typeFilter]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiErrorMessage(e)); } };
  const addToLeads = async (n) => {
    try { const { data } = await discoveryApi.addToLeads(n.payload.source_establishment_id, {}); toast.success("Added to leads"); navigate(`/leads/${data.organisation_id}`); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const grouped = items.reduce((acc, n) => {
    const day = new Date(n.created_at).toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "short" });
    (acc[day] = acc[day] || []).push(n); return acc;
  }, {});

  return (
    <div className="mx-auto max-w-2xl px-4 py-5 md:py-8">
      <h1 className="mb-3 font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">Notifications</h1>

      <div className="mb-2 flex flex-wrap gap-1.5" data-testid="notif-type-chips">
        {[["", "All types"], ["fsa_new_caterctrl_prospect", "New Prospect"], ["fhrs_rating_changed", "Rating Changed"]].map(([v, l]) => (
          <Chip key={v} active={typeFilter === v} onClick={() => setTypeFilter(v)} testid={`notif-type-${v || "all"}`}>{l}</Chip>
        ))}
      </div>
      <div className="mb-4 flex flex-wrap gap-1.5" data-testid="notif-status-chips">
        {[["unread", "Unread"], ["read", "Read"], ["snoozed", "Snoozed"], ["dismissed", "Dismissed"], ["", "All"]].map(([v, l]) => (
          <Chip key={v} active={statusFilter === v} onClick={() => setStatusFilter(v)} testid={`notif-status-${v || "all"}`}>{l}</Chip>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-28 animate-pulse rounded-xl border border-slate-200 bg-white" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState testid="notifications-empty" icon={Bell} title="No new CaterCTRL prospects"
          description="Nothing in your monitored areas right now. Set up areas and preferences to get alerted." />
      ) : (
        Object.entries(grouped).map(([day, notes]) => (
          <div key={day} className="mb-5">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">{day}</p>
            <div className="space-y-2.5">
              {notes.map((n) => (
                <div key={n.id} data-testid={`notification-${n.id}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-heading text-sm font-bold text-slate-800">{n.payload.business_name}</p>
                      <p className="mt-0.5 text-xs text-slate-500">{n.payload.source_business_type}{n.payload.venue_type && n.payload.venue_type !== "other" ? ` · ${labelize(n.payload.venue_type)}` : ""}</p>
                      <p className="mt-0.5 truncate text-xs text-slate-400">{[n.payload.address_line_1, n.payload.post_code].filter(Boolean).join(", ")}</p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">{TYPE_LABEL[n.notification_type]}</span>
                      {(() => { const src = n.payload.discovered_by || "manual"; return (
                        <span data-testid={`notif-source-${n.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${src === "scheduler" ? "bg-sky-100 text-sky-700" : "bg-violet-100 text-violet-700"}`}>
                          {src === "scheduler" ? "Auto" : "Manual"}
                        </span>
                      ); })()}
                      <PriorityBadge bucket={n.payload.fhrs_priority_bucket} testid={`notif-bucket-${n.id}`} />
                      <span className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-500">FSA rating {n.payload.source_rating_value ?? "—"}</span>
                    </div>
                  </div>
                  {n.notification_type === "fhrs_rating_changed" && (
                    <p className="mt-1 text-xs font-semibold text-amber-700">Rating changed {n.payload.from_rating} → {n.payload.to_rating}</p>
                  )}
                  {n.payload.reasons?.length > 0 && (
                    <p className="mt-1 text-[11px] text-slate-400">{n.payload.reasons.join(" · ")}</p>
                  )}
                  <p className="mt-1 text-[11px] text-slate-400">First seen by LeadCTRL {new Date(n.payload.first_seen_by_leadctrl_at).toLocaleDateString("en-GB")}</p>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <RowAction testid={`notif-view-${n.id}`} icon={Building2} label="View" onClick={() => navigate(`/discover?fsa=${n.payload.source_establishment_id}`)} />
                    <RowAction testid={`notif-add-${n.id}`} icon={MapPin} label="Add to Leads" onClick={() => addToLeads(n)} />
                    <RowAction testid={`notif-map-${n.id}`} icon={MapPin} label="Map" onClick={() => navigate(`/map?lat=${n.payload.latitude}&lng=${n.payload.longitude}`)} />
                    {isManager && <RowAction testid={`notif-assign-${n.id}`} icon={UserCheck} label="Assign" onClick={() => setAssignFor(n)} />}
                    <RowAction testid={`notif-read-${n.id}`} icon={Check} label="Read" onClick={() => act(() => notificationsApi.read(n.id), "Marked read")} />
                    <RowAction testid={`notif-snooze-${n.id}`} icon={Clock} label="Snooze" onClick={() => setSnoozeFor(n)} />
                    <RowAction testid={`notif-dismiss-${n.id}`} icon={X} label="Dismiss" onClick={() => act(() => notificationsApi.dismiss(n.id), "Dismissed")} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      <SnoozeSheet notif={snoozeFor} onClose={() => setSnoozeFor(null)} onDone={load} />
      <SignalAssignSheet notif={assignFor} onClose={() => setAssignFor(null)} onDone={load} />
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${active ? "border-teal-600 bg-teal-600 text-white" : "border-slate-200 bg-white text-slate-600"}`}>{children}</button>;
}
function RowAction({ testid, icon: Icon, label, onClick }) {
  return <button data-testid={testid} onClick={onClick} className="inline-flex min-h-[36px] items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Icon className="h-3.5 w-3.5" /> {label}</button>;
}

function SnoozeSheet({ notif, onClose, onDone }) {
  const presets = () => {
    const now = new Date();
    const h1 = new Date(now.getTime() + 3600e3);
    const tm = new Date(now); tm.setDate(tm.getDate() + 1); tm.setHours(9, 0, 0, 0);
    const nw = new Date(now); nw.setDate(nw.getDate() + 7);
    return [["1 hour", h1], ["Tomorrow", tm], ["Next week", nw]];
  };
  const snooze = async (d) => { try { await notificationsApi.snooze(notif.id, d.toISOString()); toast.success("Snoozed"); onClose(); onDone(); } catch (e) { toast.error(apiErrorMessage(e)); } };
  return (
    <Sheet open={!!notif} onClose={onClose} title="Snooze notification" testid="snooze-sheet">
      <div className="space-y-2">
        {presets().map(([label, d]) => (
          <button key={label} data-testid={`snooze-${label.replace(/\s/g, "-").toLowerCase()}`} onClick={() => snooze(d)}
            className="min-h-[48px] w-full rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50">{label}</button>
        ))}
      </div>
    </Sheet>
  );
}

function SignalAssignSheet({ notif, onClose, onDone }) {
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState("");
  useEffect(() => { if (notif) usersApi.list().then((r) => setUsers(r.data.items)).catch(() => {}); }, [notif]);
  const submit = async () => {
    try { await signalsApi.assign(notif.signal_id, sel); toast.success("Assigned"); onClose(); onDone(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  return (
    <Sheet open={!!notif} onClose={onClose} title="Assign to rep" testid="signal-assign-sheet">
      <select data-testid="signal-assign-select" value={sel} onChange={(e) => setSel(e.target.value)} className="mb-4 min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm">
        <option value="">Select a rep…</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.display_name || u.name}</option>)}
      </select>
      <button data-testid="signal-assign-submit" onClick={submit} disabled={!sel} className="min-h-[48px] w-full rounded-lg bg-teal-700 font-semibold text-white hover:bg-teal-800 disabled:opacity-60">Assign</button>
    </Sheet>
  );
}
